import type { CachedSchema, TableMeta, ViewMeta, FunctionMeta } from '../global-schema-store';

/**
 * Shared helpers that iterate the global schema cache. Every schema-side
 * provider does the same nested walk, so DRY it up here.
 */

export interface TableLocation {
  connectionId: string;
  database: string;
  schema: string;
  table: TableMeta;
}

export interface ViewLocation {
  connectionId: string;
  database: string;
  schema: string;
  view: ViewMeta;
}

export interface FunctionLocation {
  connectionId: string;
  database: string;
  schema: string;
  function: FunctionMeta;
}

export interface ColumnLocation extends TableLocation {
  columnName: string;
  columnType: string;
}

export function* walkTables(schemas: CachedSchema[]): Generator<TableLocation> {
  for (const cache of schemas) {
    for (const db of cache.metadata.databases ?? []) {
      for (const s of db.schemas) {
        for (const t of s.tables) {
          yield { connectionId: cache.connectionId, database: db.name, schema: s.name, table: t };
        }
      }
    }
  }
}

export function* walkViews(schemas: CachedSchema[]): Generator<ViewLocation> {
  for (const cache of schemas) {
    for (const db of cache.metadata.databases ?? []) {
      for (const s of db.schemas) {
        for (const v of s.views) {
          yield { connectionId: cache.connectionId, database: db.name, schema: s.name, view: v };
        }
      }
    }
  }
}

export function* walkFunctions(schemas: CachedSchema[]): Generator<FunctionLocation> {
  for (const cache of schemas) {
    for (const db of cache.metadata.databases ?? []) {
      for (const s of db.schemas) {
        for (const f of s.functions) {
          yield { connectionId: cache.connectionId, database: db.name, schema: s.name, function: f };
        }
      }
    }
  }
}

export function* walkColumns(schemas: CachedSchema[]): Generator<ColumnLocation> {
  for (const loc of walkTables(schemas)) {
    for (const col of loc.table.columns ?? []) {
      yield { ...loc, columnName: col.name, columnType: col.type };
    }
  }
}

/**
 * Connection name lookup — the schema cache stores only IDs, the connection
 * list stores names/colors. We don't want search rows that say
 * "table in <uuid> · db". Callers pass the connections list and we resolve
 * on the fly.
 */
export function connectionLabel(
  connections: Array<{ id: string; name: string }>,
  connectionId: string,
): string {
  return connections.find((c) => c.id === connectionId)?.name ?? connectionId.slice(0, 8);
}
